import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { get, regenerate } from "../../api/Operator"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, CopyIcon, RefreshIcon, KeyIcon, InfoIcon } from "./OperatorIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY MODAL STATE */
const EMPTY = {
    id: 0,
    status: "loading",
    name: "",
    slug: "",
    token: "",
    waiting: false,
}


/* Widget Operator Token */
class OperatorToken extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action */
    open = ({ id = 0, name = "", slug = "" }) => {

        this.setState({ visible: true, ...EMPTY, id: parseInt(id) ? parseInt(id) : 0, name, slug }, () => {
            this.load()
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Load Action — the token never travels with the list */
    load = () => {

        /* Fields */
        const { id } = this.state

        /* Set Status */
        this.setState({ status: "loading" })

        /* Send to REST API */
        get({ id }).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", token: utils.checkValue(response.data.operator.token, "") })
            }
            else {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(this.message(response), "error"))
                this.close()
            }
        }).catch(() => {
            this.setState({ status: "error" })
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.close()
        })

    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Copy Action — the token goes to the clipboard */
    copy = () => {

        /* Fields */
        const { token } = this.state

        /* Check Token */
        if (!token) {
            return
        }

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("operator.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(token).then(() => {
            Swal.fire(utils.notification(t("operator.tokenCopied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("operator.copyFailed"), "error"))
        })

    }

    /* Regenerate Action — every request of the old token stops working */
    regenerate = () => {

        /* Fields */
        const { id, name } = this.state

        Swal.fire({
            title: t("operator.tokenConfirm"),
            text: name,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("operator.tokenButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {
            if (result.isConfirmed) {

                /* Start Waiting */
                this.setState({ waiting: true })

                regenerate({ id }).then((response) => {
                    if (response.status === 200) {
                        this.setState({ token: utils.checkValue(response.data.token, "") })
                        Swal.fire(utils.notification(t("operator.tokenSuccess"), "success"))
                    }
                    else {
                        Swal.fire(utils.notification(this.message(response), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                }).finally(() => {
                    this.setState({ waiting: false })
                })

            }
        })
    }

    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status, token } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <div className="operator--modal-loading"><div className="operator--loading" /></div>
        }

        return (
            <div className="operator--modal-body">

                <div className="operator--section">

                    <div className="operator--section-head">
                        <div className="operator--section-title">
                            <KeyIcon size={13} />
                            {t("operator.token")}
                        </div>
                    </div>

                    <div className="operator--token">

                        <span>{token !== "" ? token : t("operator.tokenEmpty")}</span>

                        <div onClick={() => this.copy()} className="operator--token-copy" title={t("operator.copyToken")}>
                            <CopyIcon />
                        </div>

                    </div>

                    <div className="operator--hint">
                        <InfoIcon />
                        <span>{t("operator.tokenHint")}</span>
                    </div>

                </div>

            </div>
        )
    }

    /* Draw Footer */
    _footer = () => {

        /* Fields */
        const { status, waiting } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        return (
            <div className="operator--modal-footer space">

                {waiting
                    ? <div className="operator--button red"><div className="operator--loading small" /></div>
                    : <div onClick={() => this.regenerate()} className="operator--button red">
                        <RefreshIcon />
                        <span>{t("operator.tokenButton")}</span>
                    </div>
                }

                <div onClick={() => this.close()} className="operator--button ghost">{t("common.close")}</div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, name, slug } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="operator--modal small"
                overlayClassName="operator--overlay"
                closeTimeoutMS={200}
            >

                <div className="operator--modal-header">

                    <div>
                        <h2>{t("operator.tokenTitle")}</h2>
                        <p>{slug !== "" ? `${name} (${slug})` : name}</p>
                    </div>

                    <div onClick={() => this.close()} className="operator--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {/* Draw Footer */}
                {this._footer()}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(OperatorToken)
